import  { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, AlertTitle, FormControl, InputLabel, TextField, Grid, Button, Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import LoadingContainer from "../LoadingContainer/LoadingContainer";
import api_endpoints from "../../api";
import { useParams } from "react-router-dom";
import styles from "./DonorPaymentMethod.module.css";
import { useCustomTranslation } from "../../hooks/useCustomTranslation";
import LanguageSwitcher from "../Login/LanguageSwitcher";

function cloverSdkUrl() {
  const env = (process.env.REACT_APP_CLOVER_ENV || "sandbox").toLowerCase();
  return env === "production"
    ? "https://checkout.clover.com/sdk.js"
    : "https://checkout.sandbox.dev.clover.com/sdk.js";
}

function forteJsUrl() {
  const env = (process.env.REACT_APP_FORTE_ENV || "sandbox").toLowerCase();
  return env === "production"
    ? "https://api.forte.net/js/v1"
    : "https://sandbox.forte.net/api/js/v1";
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if ([...document.scripts].some((s) => s.src === src)) return resolve();
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

function ForteTextField({ id, label, forteData, placeholder = "", type = "text", disabled = false }) {
  return (
    <TextField
      id={id}
      label={label}
      fullWidth
      size="small"
      variant="outlined"
      type={type}
      disabled={disabled}
      InputLabelProps={{ shrink: true }}
      inputProps={{ "forte-data": forteData, placeholder, autoComplete: "off" }}
      sx={{
        "& .MuiOutlinedInput-root": { borderRadius: 2 },
        "& .MuiOutlinedInput-input::placeholder": { opacity: 0.6 },
      }}
    />
  );
}

function CloverField({ label, fieldId }) {
  return (
    <FormControl fullWidth>
      <InputLabel shrink sx={{ mb: 0.5, lineHeight: 1 }}>{label}</InputLabel>
      <Box
        id={fieldId}
        sx={(theme) => ({
          mt: 1,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          px: 1.25,
          height: 40,
          display: "flex",
          alignItems: "center",
          transition: "border-color 120ms ease, box-shadow 120ms ease",
          "&:focus-within": {
            borderColor: "primary.main",
            boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}`
          },
          "& iframe": { width: "100%", height: "100%" },
          "& > div": { width: "100%", height: "100%" }
        })}
      />
    </FormControl>
  );
}

function makeCloverElementIds() {
  return {
    number: `clover-card-number`,
    date: `clover-card-date`,
    cvv: `clover-card-cvv`,
    postal: `clover-card-postal`,
  };
}

const DonorPaymentMethod = () => {
  const { token } = useParams();
  const { t } = useCustomTranslation("Donor Payment Method");

  const translateMessage = useCallback(
    (key) => {
      if (typeof key !== "string") return "";
      const trimmed = key.trim();
      if (!trimmed) return "";
      const translated = t(trimmed);
      return translated !== trimmed ? translated : trimmed;
    },
    [t]
  );

  const [loading, setLoading] = useState(true);
  const [linkData, setLinkData] = useState(null);
  const [loadError, setLoadError] = useState("");

  const [cardholderName, setCardholderName] = useState("");
  const [billingAddress, setBillingAddress] = useState("");
  const [bankName, setBankName] = useState("");
  const [accountHolderName, setAccountHolderName] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [linkConsumed, setLinkConsumed] = useState(false);

  const cloverRef = useRef(null);
  const cloverElements = useRef({});
  const cloverMounted = useRef(false);

  const [cloverReady, setCloverReady] = useState(false);
  const [cloverLoading, setCloverLoading] = useState(false);
  const [cloverError, setCloverError] = useState("");

  const [forteReady, setForteReady] = useState(false);
  const [forteLoading, setForteLoading] = useState(false);
  const [forteError, setForteError] = useState("");

  const cloverElementIds = useMemo(() => makeCloverElementIds(), []);

  useEffect(() => {
    let cancelled = false;

    const fetchLink = async () => {
      try {
        setLoading(true);
        setLoadError("");
        setSuccessMessage("");
        setSubmitError("");
        setLinkConsumed(false);
        setCardholderName("");
        setBillingAddress("");
        setBankName("");
        setAccountHolderName("");
        cloverMounted.current = false;
        cloverRef.current = null;
        cloverElements.current = {};
        setCloverReady(false);
        setCloverLoading(false);
        setCloverError("");
        setForteReady(false);
        setForteLoading(false);
        setForteError("");

        const response = await api_endpoints.getDonorPaymentMethodLink(token);
        if (cancelled) return;
        setLinkData(response.data);
      } catch (error) {
        if (cancelled) return;
        const messageKey = error?.response?.data?.message;
        setLoadError(translateMessage(messageKey || "donor_payment_link_not_found"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchLink();
    return () => {
      cancelled = true;
    };
  }, [token, translateMessage]);

  useEffect(() => {
    if (!linkData || linkData.instrument_type !== "card" || linkConsumed) return;

    let cancelled = false;

    const initClover = async () => {
      try {
        setCloverLoading(true);
        setCloverError("");
        await loadScript(cloverSdkUrl());
        if (cancelled) return;
        if (!window.Clover) throw new Error("missing-clover");

        const { data } = await api_endpoints.getDonorPaymentMethodCloverPakms(token);
        if (cancelled) return;
        const apiAccessKey = data?.apiAccessKey;
        if (!apiAccessKey) throw new Error("missing-key");

        const clover = new window.Clover(apiAccessKey);
        if (cancelled) return;
        cloverRef.current = clover;

        const elements = clover.elements();
        const styles = {
          body: {
            fontFamily:
              "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
            fontSize: "14px",
          },
          input: { fontSize: "14px", color: "#1f2937" },
          "input:focus": { color: "#111827" },
        };
        cloverElements.current = {
          number: elements.create("CARD_NUMBER", styles),
          date: elements.create("CARD_DATE", styles),
          cvv: elements.create("CARD_CVV", styles),
          postal: elements.create("CARD_POSTAL_CODE", styles),
        };

        setCloverReady(true);
      } catch (error) {
        if (cancelled) return;
        console.error("[DonorPaymentMethod] Clover init", error);
        setCloverError(translateMessage("donor_payment_link_card_error"));
      } finally {
        if (!cancelled) setCloverLoading(false);
      }
    };

    initClover();
    return () => {
      cancelled = true;
    };
  }, [linkData, token, linkConsumed, translateMessage]);

  useEffect(() => {
    if (!cloverReady) return;
    if (!linkData || linkData.instrument_type !== "card") return;
    const { number, date, cvv, postal } = cloverElements.current || {};
    if (!number || !date || !cvv || !postal) return;

    const numberSelector = `#${cloverElementIds.number}`;
    const dateSelector = `#${cloverElementIds.date}`;
    const cvvSelector = `#${cloverElementIds.cvv}`;
    const postalSelector = `#${cloverElementIds.postal}`;

    if (
      !document.querySelector(numberSelector) ||
      !document.querySelector(dateSelector) ||
      !document.querySelector(cvvSelector) ||
      !document.querySelector(postalSelector)
    ) {
      return;
    }

    if (cloverMounted.current) return;

    try {
      number.mount(numberSelector);
      date.mount(dateSelector);
      cvv.mount(cvvSelector);
      postal.mount(postalSelector);
      cloverMounted.current = true;
    } catch (e) {
      console.error("[DonorPaymentMethod] Clover mount", e);
      setCloverError(translateMessage("clover_fields_not_ready"));
    }
  }, [cloverReady, linkData, cloverElementIds, translateMessage]);

  useEffect(() => {
    if (!linkData || linkData.instrument_type !== "ach" || linkConsumed) return;

    let cancelled = false;

    (async () => {
      try {
        setForteLoading(true);
        setForteError("");
        await loadScript(forteJsUrl());
        if (cancelled) return;
        const ok = typeof window.Forte !== "undefined" || typeof window.forte !== "undefined";
        if (!ok) throw new Error("missing-forte");
        setForteReady(true);
      } catch (error) {
        if (cancelled) return;
        console.error("[DonorPaymentMethod] Forte init", error);
        setForteError(translateMessage("donor_payment_link_bank_error"));
      } finally {
        if (!cancelled) setForteLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [linkData, linkConsumed, translateMessage]);

  const handleCardSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (linkConsumed) {
        setSubmitError(translateMessage("donor_payment_link_used"));
        return;
      }

      if (!cloverReady || !cloverRef.current) {
        setSubmitError(translateMessage("clover_fields_not_ready"));
        return;
      }

      try {
        setSubmitting(true);
        setSubmitError("");
        setSuccessMessage("");

        const result = await cloverRef.current.createToken();
        if (result?.errors) {
          const combined = Object.values(result.errors).join(" ");
          throw new Error(combined || "card_ingest_failed");
        }

        // match the first component: rely on result.token
        const tokenValue = result?.token;
        if (!tokenValue) {
          setSubmitError(translateMessage("missing_clover_token"));
          return;
        }

        const response = await api_endpoints.submitDonorPaymentMethodCard(token, {
          token: tokenValue,
          brand: result?.card?.brand || "",
          first6: result?.card?.first6 || "",
          last4: result?.card?.last4 || "",
          exp_month: result?.card?.exp_month || "",
          exp_year: result?.card?.exp_year || "",
          cardholderName,
          billingAddress,
        });

        const message = response?.data?.message;
        setSuccessMessage(translateMessage(message || "donor_payment_card_saved"));
        setLinkConsumed(true);
      } catch (error) {
        const messageKey = error?.response?.data?.message || error?.message;
        setSubmitError(translateMessage(messageKey || "card_ingest_failed"));
      } finally {
        setSubmitting(false);
      }
    },
    [linkConsumed, cloverReady, token, cardholderName, billingAddress, translateMessage]
  );

  const handleForteError = useCallback(
    (resp) => {
      if (linkConsumed) return;
      const messageKey = resp?.response_description;
      setSubmitError(translateMessage(messageKey || "forte_tokenization_failed"));
      setSubmitting(false);
    },
    [linkConsumed, translateMessage]
  );

  const handleForteSuccess = useCallback(
    async (resp) => {
      if (linkConsumed) return;
      try {
        setSubmitting(true);
        setSubmitError("");
        setSuccessMessage("");

        const response = await api_endpoints.submitDonorPaymentMethodAch(token, {
          one_time_token: resp?.one_time_token || resp?.onetime_token,
          last4: resp?.last_4,
          account_type: resp?.account_type,
          bank_name: bankName,
          account_holder: accountHolderName,
        });

        const message = response?.data?.message;
        setSuccessMessage(translateMessage(message || "donor_payment_bank_saved"));
        setLinkConsumed(true);
      } catch (error) {
        const messageKey = error?.response?.data?.message || error?.message;
        setSubmitError(translateMessage(messageKey || "bank_ingest_failed"));
      } finally {
        setSubmitting(false);
      }
    },
    [token, bankName, accountHolderName, linkConsumed, translateMessage]
  );

  useEffect(() => {
    if (!linkData || linkData.instrument_type !== "ach" || linkConsumed) return;
    window.__forteSuccess = handleForteSuccess;
    window.__forteError = handleForteError;
    return () => {
      delete window.__forteSuccess;
      delete window.__forteError;
    };
  }, [linkData, handleForteSuccess, handleForteError, linkConsumed]);

  const cardDisabled = linkConsumed || submitting || !cloverReady;
  const bankDisabled = linkConsumed || submitting || !forteReady;

  const renderCardForm = () => (
    <div className={styles.form}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            label={t("cardholder_name")}
            fullWidth
            size="small"
            value={cardholderName}
            onChange={(event) => setCardholderName(event.target.value)}
            disabled={linkConsumed}
            InputLabelProps={{ shrink: true }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label={t("billing_address")}
            fullWidth
            size="small"
            value={billingAddress}
            onChange={(event) => setBillingAddress(event.target.value)}
            disabled={linkConsumed}
            InputLabelProps={{ shrink: true }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
        </Grid>
        <Grid item xs={12}>
          <CloverField label={t("card_number")} fieldId={cloverElementIds.number} />
        </Grid>
        <Grid item xs={12} md={4}>
          <CloverField label={t("expiration_date")} fieldId={cloverElementIds.date} />
        </Grid>
        <Grid item xs={12} md={4}>
          <CloverField label={t("cvc")} fieldId={cloverElementIds.cvv} />
        </Grid>
        <Grid item xs={12} md={4}>
          <CloverField label={t("postal_code")} fieldId={cloverElementIds.postal} />
        </Grid>
      </Grid>

      {cloverLoading ? (
        <Alert severity="info" className={styles.alert}>
          {t("loading_secure_form")}
        </Alert>
      ) : null}
      {cloverError ? (
        <Alert severity="error" className={styles.alert}>
          {cloverError}
        </Alert>
      ) : null}

      <Button
        variant="contained"
        disabled={cardDisabled}
        sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
        onClick={handleCardSubmit}
      >
        {submitting ? t("saving") : t("save_card")}
      </Button>
    </div>
  );

  const renderBankForm = () => (
    <div className={styles.form}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            label={t("account_holder_name")}
            fullWidth
            size="small"
            variant="outlined"
            value={accountHolderName}
            onChange={(e) => setAccountHolderName(e.target.value)}
            disabled={linkConsumed}
            InputLabelProps={{ shrink: true }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label={t("bank_name_optional")}
            fullWidth
            size="small"
            variant="outlined"
            value={bankName}
            onChange={(e) => setBankName(e.target.value)}
            disabled={linkConsumed}
            InputLabelProps={{ shrink: true }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <ForteTextField id="forte-routing" label={t("routing_number")} forteData="routing_number" placeholder="123456789" disabled={linkConsumed} />
        </Grid>
        <Grid item xs={12} md={3}>
          <ForteTextField id="forte-account" label={t("account_number")} forteData="account_number" placeholder="000123456789" disabled={linkConsumed} />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label={t("account_type")}
            fullWidth
            size="small"
            select
            defaultValue="c"
            variant="outlined"
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ "forte-data": "account_type" }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            disabled={linkConsumed}
          >
            <option value="c">{t("checking")}</option>
            <option value="s">{t("savings")}</option>
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <Button
            type="submit"
            variant="contained"
            disabled={bankDisabled}
            sx={{ borderRadius: 2, px: 2.5, textTransform: "none", fontWeight: 600 }}
            forte-api-login-id={process.env.REACT_APP_FORTE_API_LOGIN_ID || ""}
            forte-callback-success="__forteSuccess"
            forte-callback-error="__forteError"
            forte-environment={(process.env.REACT_APP_FORTE_ENV || "sandbox").toLowerCase()}
            onClick={() => {
              setSubmitError("");
              setSuccessMessage("");
            }}
          >
            {submitting ? t("saving") : t("save_account")}
          </Button>
        </Grid>
      </Grid>

      {forteLoading ? (
        <Alert severity="info" className={styles.alert}>
          {t("loading_secure_form")}
        </Alert>
      ) : null}
      {forteError ? (
        <Alert severity="error" className={styles.alert}>
          {forteError}
        </Alert>
      ) : null}
    </div>
  );

  const renderForm = () => {
    if (!linkData) return null;
    if (linkData.instrument_type === "card") return renderCardForm();
    if (linkData.instrument_type === "ach") return renderBankForm();
    return (
      <Alert severity="error" className={styles.alert}>
        {t("donor_payment_link_wrong_instrument")}
      </Alert>
    );
  };

  return (
    <div className={styles.main_container}>
      <div className={styles.right_panel}>
        <LanguageSwitcher />
        <div className="flex flex-1 rounded-3xl bg-white w-1/2 min-w-[400px] max-w-[600px] p-12 flex-col">
          <div className="flex flex-1 justify-center items-center flex-col">
            <div className="text-xl px-10 text-center">
              {t("payment_method_for")}{" "}
              <span className="font-semibold text-2xl">
                {linkData?.organization?.legal_name || t("your_organization")}
              </span>{" "}
              {t("powered_by")}
            </div>
            <div className="flex w-[200px] mb-2">
              <img className="object-contain" src={process.env.PUBLIC_URL + "/Kiosk-Donations.svg"} alt="Kiosk Donations" />
            </div>

            {!successMessage && !loadError ? (
              <LoadingContainer loading={loading}>
                {renderForm()}
              </LoadingContainer>
            ) : null}

            {submitError && !successMessage && !loadError ? (
              <Alert severity="error" className="mt-6 w-full">
                {submitError}
              </Alert>
            ) : null}

            {successMessage ? (
              <div className="flex flex-1 justify-center items-center">
                <Alert severity="success" className="flex flex-1 justify-center items-center flex-col text-center">
                  <AlertTitle>{t("thank_you_for_interest")}</AlertTitle>
                  <p>{successMessage}</p>
                  <p>{t("you_can_close_page")}</p>
                </Alert>
              </div>
            ) : null}

            {loadError ? (
              <div className="flex flex-1 justify-center items-center">
                <Alert severity="error" className="flex flex-1 justify-center items-center flex-col text-center">
                  <AlertTitle>{t("invalid_link")}</AlertTitle>
                  <p>{loadError}</p>
                  <p>{t("please_close_page")}</p>
                </Alert>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonorPaymentMethod;